import { Box, Card, Typography } from "@mui/material";
import RenderMerchantDisplay from "components/common/RenderMerchantDisplay";
import dayjs from "utils/dayjs";
import { formatCashFull } from "utils/formatCash";
import { isReceiptTransactionDateMissing } from "utils/receipts/receiptFormat";
import { transactionMerchantName } from "utils/transactions/transactionRow";
import { RenderCompliance } from "components/common/RenderCompliance";
import { useTranslation } from "react-i18next";
import type { TransactionNode } from "./ReceiptConnectDrawer";

/**
 * Success state of the admin connect-receipt drawer. The Close action lives in
 * the drawer footer (see ReceiptConnectDrawer), not here.
 */
const ReceiptConnectedSuccess = ({
  connectedTransaction,
}: {
  connectedTransaction: TransactionNode;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-7)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-2)",
        }}
      >
        <Typography variant="h6">{t("Your receipt is connected!")}</Typography>
        <Typography
          variant="body1"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {t("verifyTransactionReceiptNotice")}
        </Typography>
      </Box>
      <Box>
        <Typography variant="h6">{t("Connected Transaction")}</Typography>
      </Box>
      <Card
        variant="outlined"
        sx={{ width: "100%", padding: "var(--space-7)" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
            flexDirection: "column",
          }}
        >
          <RenderMerchantDisplay
            merchant={transactionMerchantName(connectedTransaction)}
            url={connectedTransaction.merchantData?.url ?? undefined}
            logo={connectedTransaction.merchantData?.logo ?? undefined}
            cachedLogo={
              connectedTransaction.merchantData?.cachedLogo ?? undefined
            }
            withTitle={false}
            withLink={false}
            width={40}
            logoType=""
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-3)",
              textAlign: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h4" component="span">
              {transactionMerchantName(connectedTransaction)}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-2)",
              }}
            >
              <Typography
                variant="subtitle2"
                component="span"
                sx={{ color: "var(--color-text-tertiary)" }}
              >
                {isReceiptTransactionDateMissing(
                  connectedTransaction.transactionDate,
                )
                  ? "-"
                  : dayjs(connectedTransaction.transactionDate).format(
                      "MMM D, YYYY",
                    )}
              </Typography>
              <Typography variant="subtitle2" component="span">
                {formatCashFull(
                  connectedTransaction.amount,
                  connectedTransaction.currency,
                )}
              </Typography>
            </Box>
            <RenderCompliance
              policyStatus={connectedTransaction.policyStatus}
              policyStatusDisplay={connectedTransaction.policyStatusDisplay}
              sx={{ padding: "var(--space-2) 0" }}
            />
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default ReceiptConnectedSuccess;
